import {
  createApiFetch,
  resolveDevelopmentApiBaseUrl,
} from "@kindtutor/auth-client";

import {
  addAttributionToInternalUrl,
  readLeadAttribution,
} from "./lead-attribution";
import { installCloudflareWebAnalytics } from "./web-analytics";

installCloudflareWebAnalytics(
  document,
  import.meta.env.VITE_CLOUDFLARE_WEB_ANALYTICS_TOKEN,
);

const apiFetch = createApiFetch("landing/0.1.0");
const isEnglish = document.documentElement.lang === "en";
const copy = isEnglish
  ? {
      unavailable:
        "The form is temporarily unavailable. Email hi@kindtutor.app.",
      verificationExpired: "Verification expired. Please confirm it again.",
      verificationFailed:
        "Verification could not start. Refresh the page or contact us.",
      verificationRequired:
        "Complete the anti-spam verification and try again.",
      submitFallback: "Send message",
      submitting: "Sending…",
      success:
        "Thank you! We received your message and will reply within one business day.",
      sendFailed:
        "The message could not be sent. Try again or email hi@kindtutor.app.",
      openMenu: "Open menu",
      closeMenu: "Close menu",
      checking: "Checking services…",
      checkingHint: "This should take a few seconds.",
      servicesOk: "All essential services are operational",
      servicesOkDescription:
        "The API, configuration data and material storage are responding correctly.",
      checked: "Last checked",
      servicesError: "Some services may be unavailable",
      servicesErrorDescription:
        "Check again shortly. If the problem continues, email hi@kindtutor.app.",
      attempted: "Last attempt",
      locale: "en-GB",
    }
  : {
      unavailable:
        "Formularz jest chwilowo niedostępny. Napisz na hi@kindtutor.app.",
      verificationExpired: "Weryfikacja wygasła. Potwierdź formularz ponownie.",
      verificationFailed:
        "Nie udało się uruchomić weryfikacji. Odśwież stronę lub napisz do nas.",
      verificationRequired:
        "Potwierdź weryfikację antyspamową i spróbuj ponownie.",
      submitFallback: "Wyślij wiadomość",
      submitting: "Wysyłanie…",
      success:
        "Dziękujemy! Otrzymaliśmy wiadomość i odezwiemy się w ciągu 1 dnia roboczego.",
      sendFailed:
        "Nie udało się wysłać wiadomości. Spróbuj ponownie lub napisz na hi@kindtutor.app.",
      openMenu: "Otwórz menu",
      closeMenu: "Zamknij menu",
      checking: "Sprawdzamy usługi…",
      checkingHint: "To powinno potrwać kilka sekund.",
      servicesOk: "Wszystkie podstawowe usługi działają",
      servicesOkDescription:
        "API, dane konfiguracyjne i materiały odpowiadają prawidłowo.",
      checked: "Ostatnie sprawdzenie",
      servicesError: "Część usług może być niedostępna",
      servicesErrorDescription:
        "Ponów sprawdzenie za chwilę. Jeżeli problem trwa, napisz na hi@kindtutor.app.",
      attempted: "Ostatnia próba",
      locale: "pl-PL",
    };

const apiBaseURL = resolveDevelopmentApiBaseUrl(
  import.meta.env.VITE_API_BASE_URL ??
    (import.meta.env.DEV ? "http://localhost:8787" : window.location.origin),
  window.location.hostname,
  import.meta.env.DEV,
).replace(/\/$/, "");

const TURNSTILE_ACTION = "public-lead-v1";
const DEVELOPMENT_TURNSTILE_SITE_KEY = "1x00000000000000000000AA";
const turnstileSiteKey =
  import.meta.env.VITE_TURNSTILE_SITE_KEY?.trim() ||
  (import.meta.env.DEV ? DEVELOPMENT_TURNSTILE_SITE_KEY : "");

const mainContent = document.querySelector<HTMLElement>("main#main");
const skipLink = document.querySelector<HTMLAnchorElement>(
  'a.skip[href="#main"]',
);
if (mainContent && skipLink) {
  mainContent.tabIndex = -1;
  skipLink.addEventListener("click", () => {
    window.requestAnimationFrame(() => mainContent.focus());
  });
}

type TurnstileApi = {
  render(
    container: HTMLElement,
    options: {
      sitekey: string;
      action: string;
      size: "flexible";
      callback: () => void;
      "expired-callback": () => void;
      "error-callback": () => void;
    },
  ): string;
  reset(widgetId: string): void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const turnstileWidgets = new WeakMap<HTMLFormElement, string>();
const pageAttribution = readLeadAttribution({
  pathname: window.location.pathname,
  search: window.location.search,
  referrer: document.referrer,
  currentHostname: window.location.hostname,
});

document.querySelectorAll<HTMLAnchorElement>("a[href]").forEach((anchor) => {
  const target = addAttributionToInternalUrl(
    new URL(anchor.href, window.location.href),
    window.location.origin,
    pageAttribution,
  );
  if (target.origin === window.location.origin) anchor.href = target.toString();
});

function setFormStatus(
  form: HTMLFormElement,
  message: string,
  state: "pending" | "success" | "error",
) {
  const status = form.querySelector<HTMLElement>("[data-form-status]");
  if (!status) return;
  status.textContent = message;
  status.dataset.state = state;
}

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      "script[data-turnstile-script]",
    );
    const script = existing ?? document.createElement("script");
    const handleLoad = () =>
      window.turnstile
        ? resolve(window.turnstile)
        : reject(new Error("Turnstile API unavailable"));
    script.addEventListener("load", handleLoad, { once: true });
    script.addEventListener(
      "error",
      () => reject(new Error("Turnstile load failed")),
      { once: true },
    );
    if (!existing) {
      script.src =
        "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.dataset.turnstileScript = "true";
      document.head.append(script);
    }
  });
}

async function initializeTurnstileForms() {
  const forms = [
    ...document.querySelectorAll<HTMLFormElement>("[data-contact-form]"),
  ];
  if (forms.length === 0) return;
  if (!turnstileSiteKey) {
    forms.forEach((form) => setFormStatus(form, copy.unavailable, "error"));
    return;
  }
  try {
    const turnstile = await loadTurnstile();
    forms.forEach((form) => {
      const slot = form.querySelector<HTMLElement>("[data-turnstile]");
      const submit = form.querySelector<HTMLButtonElement>(
        "[data-submit-button]",
      );
      if (!slot || !submit) return;
      const widgetId = turnstile.render(slot, {
        sitekey: turnstileSiteKey,
        action: TURNSTILE_ACTION,
        size: "flexible",
        callback: () => {
          submit.disabled = false;
          setFormStatus(form, "", "pending");
        },
        "expired-callback": () => {
          submit.disabled = true;
          setFormStatus(form, copy.verificationExpired, "error");
        },
        "error-callback": () => {
          submit.disabled = true;
          setFormStatus(form, copy.verificationFailed, "error");
        },
      });
      turnstileWidgets.set(form, widgetId);
    });
  } catch {
    forms.forEach((form) =>
      setFormStatus(form, copy.verificationFailed, "error"),
    );
  }
}

const menuButton = document.querySelector<HTMLButtonElement>("[data-menu]");
const navigation = document.querySelector<HTMLElement>("[data-navigation]");
function setNavigationOpen(open: boolean) {
  navigation?.classList.toggle("is-open", open);
  if (!menuButton) return;
  menuButton.setAttribute("aria-expanded", String(open));
  menuButton.setAttribute("aria-label", open ? copy.closeMenu : copy.openMenu);
}

menuButton?.addEventListener("click", () => {
  setNavigationOpen(!(navigation?.classList.contains("is-open") ?? false));
});

document.addEventListener("keydown", (event) => {
  if (event.key !== "Escape" || !navigation?.classList.contains("is-open")) {
    return;
  }
  setNavigationOpen(false);
  menuButton?.focus();
});

document
  .querySelectorAll<HTMLDetailsElement>("details[data-faq]")
  .forEach((detail) => {
    detail.addEventListener("toggle", () => {
      if (!detail.open) return;
      document
        .querySelectorAll<HTMLDetailsElement>("details[data-faq]")
        .forEach((other) => {
          if (other !== detail) other.open = false;
        });
    });
  });

async function submitContactForm(form: HTMLFormElement) {
  const status = form.querySelector<HTMLElement>("[data-form-status]");
  const submitButton = form.querySelector<HTMLButtonElement>(
    "[data-submit-button]",
  );
  if (!form.reportValidity() || !submitButton) return;

  const values = new FormData(form);
  const turnstileToken = String(
    values.get("cf-turnstile-response") ?? "",
  ).trim();
  if (!turnstileToken) {
    setFormStatus(form, copy.verificationRequired, "error");
    return;
  }
  const idempotencyKey = form.dataset.idempotencyKey ?? crypto.randomUUID();
  form.dataset.idempotencyKey = idempotencyKey;
  const submitLabel = submitButton.textContent?.trim() || copy.submitFallback;
  submitButton.disabled = true;
  submitButton.textContent = copy.submitting;
  if (status) {
    status.textContent = "";
    status.dataset.state = "pending";
  }

  try {
    const response = await apiFetch(`${apiBaseURL}/v1/public/leads`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "Idempotency-Key": idempotencyKey,
      },
      body: JSON.stringify({
        organizationName: String(values.get("school") ?? ""),
        contactName: String(values.get("name") ?? ""),
        contactEmail: String(values.get("email") ?? ""),
        contactRole: String(values.get("role") ?? "") || null,
        estimatedDevices: Number(values.get("estimatedDevices") ?? 1),
        message: String(values.get("message") ?? "") || null,
        consent: values.get("consent") === "on",
        website: String(values.get("website") ?? ""),
        turnstileToken,
        ...pageAttribution,
      }),
    });
    if (!response.ok)
      throw new Error(`Lead submission failed: ${response.status}`);

    form.reset();
    const widgetId = turnstileWidgets.get(form);
    if (widgetId && window.turnstile) window.turnstile.reset(widgetId);
    delete form.dataset.idempotencyKey;
    if (status) {
      status.textContent = copy.success;
      status.dataset.state = "success";
    }
  } catch {
    const widgetId = turnstileWidgets.get(form);
    if (widgetId && window.turnstile) window.turnstile.reset(widgetId);
    if (status) {
      status.textContent = copy.sendFailed;
      status.dataset.state = "error";
    }
  } finally {
    submitButton.disabled = turnstileWidgets.has(form);
    submitButton.textContent = submitLabel;
  }
}

document
  .querySelectorAll<HTMLFormElement>("[data-contact-form]")
  .forEach((form) => {
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      void submitContactForm(form);
    });
  });

void initializeTurnstileForms();

type PublicDependencyHealth = {
  status: "ok" | "degraded";
  dependencies: { database: "ok" | "error"; materials: "ok" | "error" };
  timestamp: string;
};

async function refreshPlatformStatus(status: HTMLElement) {
  const heading = status.querySelector<HTMLElement>("h2");
  const description = status.querySelector<HTMLElement>("p");
  const checked = status.querySelector<HTMLElement>("[data-status-checked]");
  const button = status.querySelector<HTMLButtonElement>(
    "[data-status-refresh]",
  );
  if (!heading || !description || !button) return;
  button.disabled = true;
  heading.textContent = copy.checking;
  description.textContent = copy.checkingHint;
  status.dataset.state = "pending";
  try {
    const response = await apiFetch(`${apiBaseURL}/v1/health/dependencies`, {
      headers: { Accept: "application/json" },
    });
    const health = (await response.json()) as PublicDependencyHealth;
    if (!response.ok || health.status !== "ok") throw new Error("degraded");
    heading.textContent = copy.servicesOk;
    description.textContent = copy.servicesOkDescription;
    status.dataset.state = "ok";
    if (checked) {
      checked.textContent = `${copy.checked}: ${new Date(health.timestamp).toLocaleString(copy.locale)}`;
    }
  } catch {
    heading.textContent = copy.servicesError;
    description.textContent = copy.servicesErrorDescription;
    status.dataset.state = "error";
    if (checked) {
      checked.textContent = `${copy.attempted}: ${new Date().toLocaleString(copy.locale)}`;
    }
  } finally {
    button.disabled = false;
  }
}

document
  .querySelectorAll<HTMLElement>("[data-platform-status]")
  .forEach((status) => {
    const refresh = status.querySelector<HTMLButtonElement>(
      "[data-status-refresh]",
    );
    refresh?.addEventListener("click", () => {
      void refreshPlatformStatus(status);
    });
    void refreshPlatformStatus(status);
  });
